import { Dispatch } from 'redux';
import {
  FETCH_FILTER_OPTIONS_REQUEST,
  FETCH_FILTER_OPTIONS_SUCCESS,
  FETCH_FILTER_OPTIONS_FAILURE,
} from './actionTypes';
import { apiFetch } from '../services/api';
import { RootState } from '../types';

// ─── Types ───────────────────────────────────────────────────────

export type FilterOptionItem = { id: string; label: string };

export interface FilterOptionsData {
  // From /api/metadata
  accountManagers: FilterOptionItem[];
  zones: FilterOptionItem[];
  countries: FilterOptionItem[];
  // From /api/metadata — tags
  tags: FilterOptionItem[];
  skills: FilterOptionItem[];
  // From /api/candidates/distinct-values
  languageProficiencies: string[];
  sources: string[];
  utmMediums: string[];
  utmTerms: string[];
  // UTM Content = ToFu job postings ({ id: job_uid, label: job name }), not raw
  // distinct metadata.utm_content (which is polluted with legacy emails).
  utmContents: FilterOptionItem[];
  roles: string[];
  rejectedByNames: string[];
  // Full metadata for transforms
  metadataRaw: Record<string, any>;
}

export interface FetchFilterOptionsRequestAction {
  type: typeof FETCH_FILTER_OPTIONS_REQUEST;
}

export interface FetchFilterOptionsSuccessAction {
  type: typeof FETCH_FILTER_OPTIONS_SUCCESS;
  payload: FilterOptionsData;
}

export interface FetchFilterOptionsFailureAction {
  type: typeof FETCH_FILTER_OPTIONS_FAILURE;
  payload: string;
}

export type FilterOptionsAction =
  | FetchFilterOptionsRequestAction
  | FetchFilterOptionsSuccessAction
  | FetchFilterOptionsFailureAction;

// ─── Thunk ───────────────────────────────────────────────────────

export function fetchFilterOptions(refresh = false) {
  return async (dispatch: Dispatch<FilterOptionsAction>, getState: () => RootState) => {
    dispatch({ type: FETCH_FILTER_OPTIONS_REQUEST });

    try {
      const environment = getState().environment.current;

      // `refresh` bypasses the server-side 8h distinct-values cache (FiltersDrawer
      // refresh button) so a freshly-added source / medium / term shows immediately.
      const distinctUrl = `/api/candidates/distinct-values${refresh ? '?refresh=true' : ''}`;

      // Fetch all endpoints in parallel
      const [metadataRes, distinctRes, whitelistedRes] = await Promise.all([
        apiFetch('/api/metadata', { environment }),
        apiFetch(distinctUrl, { environment }),
        apiFetch('/api/candidates/whitelisted-sources', { environment }),
      ]);

      const meta = metadataRes.data ?? metadataRes;
      const distinct = distinctRes.data ?? distinctRes;
      const whitelisted = whitelistedRes.data ?? whitelistedRes;

      // Transform metadata into FilterOptionItem arrays
      const accountManagers: FilterOptionItem[] = Object.values(meta.account_managers || {}).map(
        (am: any) => ({ id: am.id, label: `${am.first_name} ${am.last_name}` })
      );

      const zones: FilterOptionItem[] = Object.values(meta.zones || {}).map(
        (z: any) => ({ id: z.id ?? z.name, label: z.name })
      );

      const countries: FilterOptionItem[] = Object.values(meta.countries || {}).map(
        (c: any) => ({ id: c.id, label: c.name })
      );
      countries.sort((a, b) => a.label.localeCompare(b.label));

      const allTags: FilterOptionItem[] = Object.values(meta.metadata_tags || {})
        .filter((t: any) => t.tag_type === 'tool')
        .map((t: any) => ({ id: t.id, label: t.name }));
      allTags.sort((a, b) => a.label.localeCompare(b.label));

      const allSkills: FilterOptionItem[] = Object.values(meta.metadata_tags || {})
        .filter((t: any) => t.tag_type === 'skill')
        .map((t: any) => ({ id: t.id, label: t.name }));
      allSkills.sort((a, b) => a.label.localeCompare(b.label));

      dispatch({
        type: FETCH_FILTER_OPTIONS_SUCCESS,
        payload: {
          accountManagers,
          zones,
          countries,
          tags: allTags,
          skills: allSkills,
          languageProficiencies: distinct.language_proficiencies || [],
          sources: whitelisted.sources || distinct.sources || [],
          utmMediums: distinct.utm_mediums || [],
          utmTerms: distinct.utm_terms || [],
          utmContents: distinct.utm_contents || [],
          roles: distinct.roles || [],
          rejectedByNames: distinct.rejected_by_names || [],
          metadataRaw: meta,
        },
      });
    } catch (error: any) {
      dispatch({
        type: FETCH_FILTER_OPTIONS_FAILURE,
        payload: error.message || 'Failed to fetch filter options',
      });
    }
  };
}
